import { useState, useRef, useCallback, useEffect } from 'react';
import { RefreshCw, CheckCircle, AlertCircle } from 'lucide-react';
import { financialStatementApi } from '../../services/financialStatementApi';
import { ConfirmModal } from '../ui/ConfirmModal';

interface Props {
    company: string;
    onComplete: () => void;
    canEdit: boolean;
}

type RefreshState = 'idle' | 'running' | 'succeeded' | 'failed';

export function RefreshButton({ company, onComplete, canEdit }: Props) {
    const [state, setState] = useState<RefreshState>('idle');
    const [message, setMessage] = useState('');
    const [showConfirm, setShowConfirm] = useState(false);
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const cleanup = useCallback(() => {
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
    }, []);

    useEffect(() => cleanup, [cleanup]);

    const handleRefresh = async () => {
        setShowConfirm(false);
        setState('running');
        setMessage('Starting pipeline...');

        try {
            const result = await financialStatementApi.triggerRefresh(company);
            const runId = result.runId;
            setMessage(`Pipeline queued (${runId.substring(0, 8)}...)`);

            // Poll every 15 seconds
            pollRef.current = setInterval(async () => {
                try {
                    const status = await financialStatementApi.getRefreshStatus(runId);
                    setMessage(`Status: ${status.status}`);

                    if (status.status === 'Succeeded') {
                        cleanup();
                        setState('succeeded');
                        setMessage('Data refreshed successfully');
                        onComplete();
                        setTimeout(() => setState('idle'), 5000);
                    } else if (status.status === 'Failed' || status.status === 'Cancelled') {
                        cleanup();
                        setState('failed');
                        setMessage(status.message || 'Pipeline failed');
                    }
                } catch {
                    // Continue polling on error
                }
            }, 15000);
        } catch (err) {
            setState('failed');
            setMessage(err instanceof Error ? err.message : 'Failed to trigger refresh');
        }
    };

    if (!canEdit) return null;

    return (
        <div className="inline-flex items-center gap-2">
            <button
                onClick={() => setShowConfirm(true)}
                disabled={state === 'running'}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium shadow-sm ${
                    state === 'running'
                        ? 'bg-blue-100 text-blue-700 cursor-wait'
                        : state === 'succeeded'
                        ? 'bg-green-100 text-green-700'
                        : state === 'failed'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-white text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
                }`}
            >
                {state === 'running' ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                ) : state === 'succeeded' ? (
                    <CheckCircle className="h-4 w-4" />
                ) : state === 'failed' ? (
                    <AlertCircle className="h-4 w-4" />
                ) : (
                    <RefreshCw className="h-4 w-4" />
                )}
                {state === 'running' ? 'Refreshing...' : 'Refresh Data'}
            </button>

            {message && state !== 'idle' && (
                <span className="text-xs text-gray-500">{message}</span>
            )}

            {showConfirm && (
                <ConfirmModal
                    title="Refresh Data"
                    message={`Refresh data for ${company.toUpperCase()}? This may take several minutes.`}
                    confirmLabel="Refresh"
                    variant="warning"
                    onConfirm={handleRefresh}
                    onCancel={() => setShowConfirm(false)}
                />
            )}
        </div>
    );
}
